import { Component } from '@theme/component';

/**
 * @typedef {Object} AxDiscountGateRefs
 * @property {HTMLInputElement} input - Membership number input
 * @property {HTMLElement} error - Error message container
 * @property {HTMLElement} form - Entry step wrapper
 * @property {HTMLElement} reveal - Code reveal wrapper
 * @property {HTMLButtonElement} copyButton - Copy code button
 */

/**
 * Partner discount gate: validates a membership entry then reveals the discount code.
 * @extends {Component<AxDiscountGateRefs>}
 */
class AxDiscountGate extends Component {
	requiredRefs = ['input', 'error', 'form', 'reveal', 'copyButton'];

	/** @type {number | undefined} */
	#copyTimeout;

	disconnectedCallback() {
		super.disconnectedCallback();
		clearTimeout(this.#copyTimeout);
	}

	/**
	 * @param {KeyboardEvent} event
	 */
	handleKeydown(event) {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		this.#unlock();
	}

	handleSubmit() {
		this.#unlock();
	}

	async handleCopy() {
		const { copyButton } = this.refs;
		const code = this.dataset.code ?? '';

		try {
			await navigator.clipboard.writeText(code);
		} catch {
			this.#copyFallback(code);
		}

		const previous = copyButton.textContent;
		copyButton.textContent = this.dataset.copiedLabel || previous;
		clearTimeout(this.#copyTimeout);
		this.#copyTimeout = setTimeout(() => {
			copyButton.textContent = previous;
		}, 2000);
	}

	/**
	 * Clipboard fallback for browsers without the async API.
	 * @param {string} code
	 */
	#copyFallback(code) {
		const textarea = document.createElement('textarea');
		textarea.value = code;
		textarea.style.position = 'fixed';
		textarea.style.opacity = '0';
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand('copy');
		textarea.remove();
	}

	#unlock() {
		const { input, error, form, reveal, copyButton } = this.refs;
		const value = input.value.trim();
		const secret = (this.dataset.secret || '').trim();

		if (!value) {
			error.textContent = this.dataset.errorEmpty || '';
			return;
		}

		if (secret && value.toLowerCase() !== secret.toLowerCase()) {
			error.textContent = this.dataset.errorInvalid || '';
			return;
		}

		error.textContent = '';
		form.hidden = true;
		reveal.hidden = false;
		copyButton.focus();
	}
}

customElements.define('ax-discount-gate', AxDiscountGate);
